import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertVariant,
  Button,
  ButtonVariant,
  DatePickerType,
  Flyout,
  notifySuccess,
  PhoneInput,
  Select,
  TextInput,
  YearsOrder,
} from '@brasshq/ui-kit';
import cx from 'classnames';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import { CURRENT_YEAR, GENDER_OPTIONS } from 'src/data/constants';
import DatePicker from 'src/sharedComponents/DatePicker/DatePicker';
import RolePrivileges from 'src/sharedComponents/RolePrivileges';
import { Role } from 'src/types';
import { TeamMember } from 'src/types/entities/administration';
import { objectDifference } from 'src/utils/genericUtils';

import { sendTeamInvite, updateTeamMember } from '../../functions';
import { RDTSendTeamInvite } from '../../types';
import { sendTeamInviteValidationSchema } from '../../validators';

import styles from './TeamMatesDetailsFlyout.module.scss';

interface TeamMatesDetailsFlyoutProps {
  tag?: string;
  isOpen: boolean;
  data: TeamMember | null;
  roles?: Role[];
  refresh: () => void;
  handleClose?: () => void;
}

const TeamMatesDetailsFlyout: React.FC<TeamMatesDetailsFlyoutProps> = props => {
  const { data, handleClose, roles, refresh, isOpen = true } = props;
  const [showRoles, setShowRoles] = useState(false);
  const isEditContext = Boolean(data && data?.id);

  const initialValues: RDTSendTeamInvite = {
    firstname: data?.firstname || '',
    lastname: data?.lastname || '',
    email: data?.email || '',
    role: data?.role?.data?.id || '',
    gender: data?.gender || '',
    phone: data?.phone || '',
    dob: data?.date_of_birth
      ? format(new Date(data.date_of_birth), 'yyyy-MM-dd')
      : null,
  };

  const validator = React.useMemo(() => {
    let validationSchema: any = sendTeamInviteValidationSchema;

    if (!isEditContext)
      validationSchema = validationSchema.omit(['dob', 'gender', 'phone']);

    return validationSchema;
  }, [isEditContext]);

  useEffect(() => {
    if (initialValues?.role) {
      setShowRoles(true);
    } else {
      setShowRoles(false);
    }
  }, [initialValues.role]);

  const handleRequestCallback = () => {
    refresh();
    notifySuccess(
      isEditContext
        ? 'Team member updated successfully'
        : 'Invitation sent successfully!',
    );
    props.handleClose && props.handleClose();
  };

  const handleSubmit = async (values: RDTSendTeamInvite) => {
    isEditContext && data?.id
      ? await updateTeamMember(
          data.id,
          objectDifference(initialValues, values),
          handleRequestCallback,
        )
      : await sendTeamInvite(values, handleRequestCallback);
  };

  return (
    <Flyout
      isOpen={isOpen}
      isMaskClear={false}
      isMaskOn
      className={styles.TeamMatesDetailsFlyout_wrapper}
      closeFlyout={handleClose}
      style={!showRoles ? { right: '-487px' } : {}}
    >
      <Formik
        validationSchema={validator}
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange
        onSubmit={handleSubmit}
      >
        {({
          values,
          isValid,
          dirty,
          handleChange,
          isSubmitting,
          setFieldValue,
          handleBlur,
          errors,
          touched,
        }) => {
          const activeRole: any =
            roles?.find(({ id }) => id === values?.role) || data?.role?.data;

          const isAnOwner = activeRole?.name === 'business.owner';

          return (
            <section className={styles.TeamMatesDetailsFlyout}>
              <section>
                <header>
                  <h5>
                    {isEditContext ? `Edit team mate` : `Invite a team mate`}
                  </h5>
                  <p>
                    {isEditContext
                      ? `Make changes to this team mate `
                      : `Send an invitation to join your company’s Brass account`}
                  </p>
                </header>
                <main>
                  <Form className={styles.TeamMatesDetailsFlyout_form}>
                    <div
                      className={cx(
                        styles.TeamMatesDetailsFlyout_form_row,
                        styles.TeamMatesDetailsFlyout_form_row_group,
                      )}
                    >
                      <TextInput
                        label='First name'
                        name='firstname'
                        value={values.firstname}
                        onChange={handleChange}
                        readOnly={isEditContext}
                        disabled={isEditContext}
                        onBlur={handleBlur}
                        errorMessage={touched.firstname ? errors.firstname : ''}
                      />
                      <TextInput
                        label='Last name'
                        name='lastname'
                        value={values.lastname}
                        onChange={handleChange}
                        readOnly={isEditContext}
                        disabled={isEditContext}
                        onBlur={handleBlur}
                        errorMessage={touched.lastname ? errors.lastname : ''}
                      />
                    </div>

                    <TextInput
                      label='Email address'
                      name='email'
                      value={values.email}
                      onChange={handleChange}
                      readOnly={isEditContext}
                      disabled={isEditContext}
                      className={cx(styles.TeamMatesDetailsFlyout_form_row)}
                      onBlur={handleBlur}
                      errorMessage={touched.email ? errors.email : ''}
                    />

                    <Select
                      className={cx(
                        styles.TeamMatesDetailsFlyout_form_row,
                        styles.TeamMatesDetailsFlyout_form_row_select,
                      )}
                      label='Assign role'
                      options={roles || []}
                      valueField='id'
                      labelField='display_name'
                      onChange={(role: Role) => {
                        setFieldValue('role', role.id);
                        if (!showRoles) {
                          setShowRoles(true);
                        }
                      }}
                      value={values.role || ''}
                      disabled={!dirty && isAnOwner}
                      showSearch
                    />

                    {isEditContext && (
                      <>
                        <PhoneInput
                          label='Phone number'
                          name='phone'
                          id='phone'
                          value={values.phone || ''}
                          onChange={handleChange}
                          className={cx(styles.TeamMatesDetailsFlyout_form_row)}
                          onBlur={handleBlur}
                          errorMessage={
                            touched.phone ? errors.phone : undefined
                          }
                        />

                        <div
                          className={cx(
                            styles.TeamMatesDetailsFlyout_form_row,
                            styles.TeamMatesDetailsFlyout_form_row_group,
                          )}
                        >
                          <Select
                            label='Gender'
                            name='gender'
                            options={GENDER_OPTIONS}
                            placeholder='Select gender'
                            onChange={option =>
                              setFieldValue('gender', option.value)
                            }
                            value={values.gender || ''}
                          />

                          <DatePicker
                            label='Date of birth'
                            id='dob'
                            alignment='right'
                            variant='light'
                            type={DatePickerType.Date}
                            placeholder='Choose date'
                            value={values.dob || ''}
                            onChange={value => setFieldValue('dob', value)}
                            startYear={CURRENT_YEAR - 70}
                            endYear={CURRENT_YEAR - 16}
                            yearsOrder={YearsOrder.DSC}
                          />
                        </div>
                      </>
                    )}

                    {isAnOwner && (
                      <Alert
                        className={styles.TeamMatesDetailsFlyout_form_owner}
                        title={`About “owner”`}
                        description='To re-assign or disable an owner’s role, you will have
                          to contact support'
                        variant={AlertVariant.Warning}
                      />
                    )}

                    <div className={styles.TeamMatesDetailsFlyout_form_actions}>
                      <Button
                        variant={ButtonVariant.Secondary}
                        className={
                          styles.TeamMatesDetailsFlyout_form_actions_button
                        }
                        onClick={e => {
                          e.preventDefault();
                          props.handleClose && props.handleClose();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant={ButtonVariant.Primary}
                        className={
                          styles.TeamMatesDetailsFlyout_form_actions_button
                        }
                        type='submit'
                        disabled={!(isValid && dirty) || isSubmitting}
                        loading={isSubmitting}
                      >
                        {isEditContext ? 'Make changes' : 'Send Invitation'}
                      </Button>
                    </div>
                  </Form>
                </main>
              </section>
              {showRoles && (
                <RolePrivileges
                  role={activeRole}
                  className={styles.TeamMatesDetailsFlyout_privileges}
                />
              )}
            </section>
          );
        }}
      </Formik>
    </Flyout>
  );
};

export default TeamMatesDetailsFlyout;
